import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  Avatar,
  Skeleton,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Send as SendIcon,
  Handshake as HandshakeIcon,
} from '@mui/icons-material';
import API from '../../API';
import CreatorDialog from '../Dashboard/Search/creatorintake';
import useAuth from '../../Hooks/use-auth';
import RosterSettingsDialog from './agencyRosterSettings';
import AgencyZapfeed from './Components/agencyZapfeed';

export default function AgencyStart() {
  const navigate = useNavigate();
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const agencyName = userInfo?.company_name;
  const userId = userInfo?.id;

  const [openCreatorDialog, setOpenCreatorDialog] = useState(false);
  const [openRosterSettings, setOpenRosterSettings] = useState(false);

  const { data: dashboard, isLoading } = useQuery(
    ['agency', 'dashboard'],
    API.agency.dashboard,
    { refetchOnWindowFocus: false }
  );

  const metrics = [
    { label: 'Creators', value: dashboard?.creators, icon: <GroupIcon />, bg: 'linear-gradient(135deg, #424242 0%, #616161 100%)' },
    { label: 'Active Campaigns', value: dashboard?.active_campaigns, icon: <AssessmentIcon />, bg: 'linear-gradient(135deg, #1E88E5 0%, #1565C0 100%)' },
    { label: 'Active Partnerships', value: dashboard?.active_partnerships, icon: <HandshakeIcon />, bg: 'linear-gradient(135deg, #8E24AA 0%, #6A1B9A 100%)' },
    { label: 'Pending Payouts', value: dashboard?.active_payouts, icon: <SendIcon />, bg: 'linear-gradient(135deg, #FB8C00 0%, #EF6C00 100%)' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Agency Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>{agencyName?.[0]}</Avatar>
          <Typography variant="body1" color="text.secondary">
            {agencyName}
          </Typography>
        </Box>
      </Box>

      {/* Metrics */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {metrics.map(({ label, value, icon, bg }) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                borderRadius: 2,
                background: bg,
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ mr: 1 }}>{icon}</Box>
                <Typography variant="subtitle1">{label}</Typography>
              </Box>
              <Typography variant="h3" sx={{ mt: 1, fontWeight: 'bold' }}>
                {isLoading ? <Skeleton width={60} /> : value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Activity Feed */}
        <Grid item xs={12} md={5}>
          <Paper elevation={3} sx={{ p: 2, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Activity Feed
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              <AgencyZapfeed />
            </Box>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={7}>
          <Paper elevation={3} sx={{ p: 2, height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} sx={{ flexGrow: 1 }}>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<AssessmentIcon />}
                  onClick={() => navigate('/dashboard')}
                  sx={{ py: 1.5, textTransform: 'none' }}
                >
                  View Reports
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<PersonAddIcon />}
                  onClick={() => setOpenCreatorDialog(true)}
                  sx={{ py: 1.5, textTransform: 'none' }}
                >
                  Add Creator
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<SettingsIcon />}
                  onClick={() => setOpenRosterSettings(true)}
                  sx={{ py: 1.5, textTransform: 'none' }}
                >
                  Roster Settings
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<GroupIcon />}
                  onClick={() => navigate(`/roster/${agencyName}`)}
                  sx={{ py: 1.5, textTransform: 'none' }}
                >
                  Your Roster
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<SendIcon />}
                  onClick={() => navigate(`/new_partner/${userId}`)}
                  sx={{ mt: 2, py: 1.5, textTransform: 'none' }}
                >
                  Invite Creators to Partner
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <CreatorDialog
        open={openCreatorDialog}
        onClose={() => setOpenCreatorDialog(false)}
        defaultManager={agencyName}
      />
      <RosterSettingsDialog
        open={openRosterSettings}
        onClose={() => setOpenRosterSettings(false)}
        agencyName={agencyName}
      />
    </Box>
  );
}